import { curry } from "lodash";
import { createSelector } from "reselect";
import { MissingSpacesState, SpacesState } from "../types";

export const getSpaces = (state: SpacesState) => state.spaces.entities;
export const getMissingSpaces = (state: MissingSpacesState) => state.missingSpaces;

/**
 * Selector that combines the results of getSpaces and getMissingSpaces.
 * The combined result is an object that includes all properties from both
 * `spaces` and `missingSpaces`, with properties from `spaces` taking precedence
 * if there are overlapping keys.
 *
 * This selector is intended for use only on admin pages.
 *
 * @returns {Object} - The combined object with properties from both spaces and missingSpaces.
 */
export const getAllSpaces = createSelector(getSpaces, getMissingSpaces, (spaces, missingSpaces) => ({
    ...missingSpaces,
    ...spaces,
}));
export const getSpace = curry((id: number, state: SpacesState) => getSpaces(state)[id]);
export const spacesList = createSelector(getSpaces, spaces => (spaces ? Object.values(spaces) : []));
export const isSpacesLoaded = (state: SpacesState) => !!getSpaces(state);
export const getIsWithoutSpaces = createSelector(
    isSpacesLoaded,
    spacesList,
    (isLoaded, spaces) => isLoaded && !spaces.length
);
export const getMenuSpaceId = (state: SpacesState) => state.spaces.spacesData.menuSpaceId;
export const getIsSpacePage = (state: SpacesState) => state.spaces.spacesData.isSpacePage;
export const getLocationSpaceId = createSelector(getMenuSpaceId, getIsSpacePage, (spaceId, isSpaceSpecificPage) =>
    isSpaceSpecificPage ? spaceId : null
);
export const getSpacesWithCanCreateProjects = createSelector(spacesList, spaces =>
    spaces.filter(space => space.CanCreateProjects)
);

export const getSpacesForInviteUsers = createSelector(spacesList, spaces =>
    spaces.filter(space => space.CanEdit || space.CanInviteUsers)
);

export const getDefaultSpaceIdForNewProject = createSelector(
    getSpacesWithCanCreateProjects,
    getMenuSpaceId,
    (list, currentSpaceId) => {
        return list.find(space => space.SpaceId === currentSpaceId)?.SpaceId ?? list[0]?.SpaceId;
    }
);

export const getCurrentSpace = createSelector(getSpaces, getMenuSpaceId, (spaces, spaceId) => spaces?.[spaceId]);

export const getCanCreateProjectInCurrentSpace = createSelector(
    getSpaces,
    getCurrentSpace,
    getIsSpacePage,
    (spaces, currentSpace, isSpacePage) => {
        if (!isSpacePage) {
            return Object.values(spaces).some(x => x.CanCreateProjects);
        }

        return currentSpace.CanCreateProjects;
    }
);

export const getCanManageSystemSettingsInCurrentSpace = createSelector(
    getCurrentSpace,
    currentSpace => currentSpace?.CanEdit ?? false
);

export const getCanManageSystemSettingsBySpaceId = createSelector(
    getSpaces,
    (_: SpacesState, spaceId: number) => spaceId,
    (spaces, spaceId) => spaces?.[spaceId]?.CanEdit
);
